import * as React from 'react';
import { Pagination } from '@components/base/navigation';
import { Text } from '@components/base/display';
import { VStack, HStack, Card } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Shared no-op handlers for static demo Pagination instances
const staticPageChange = () => {};
const staticPageSizeChange = () => {};

function PaginationShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // State for interactive examples
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [controlledPage, setControlledPage] = React.useState(5);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="pagination">
          <VStack gap={4}>
            <Pagination
              page={currentPage}
              totalPages={20}
              totalItems={487}
              pageSize={25}
              onPageChange={setCurrentPage}
              showFirstLast={playgroundProps.showFirstLast as boolean}
            />
            <Text size="sm" color="secondary">
              Current page: {currentPage} of 20
            </Text>
          </VStack>
      </ShowcasePreviewSection>

      {/* Basic Pagination */}
      <ShowcaseDemoSection
        title="Basic Pagination"
        description="Simple pagination with page numbers and prev/next buttons"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">Few pages</Text>
              <Pagination
                page={2}
                totalPages={5}
                onPageChange={staticPageChange}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Many pages (with ellipsis)</Text>
              <Pagination
                page={7}
                totalPages={20}
                onPageChange={staticPageChange}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">First page</Text>
              <Pagination
                page={1}
                totalPages={15}
                onPageChange={staticPageChange}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Last page</Text>
              <Pagination
                page={15}
                totalPages={15}
                onPageChange={staticPageChange}
              />
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* With First/Last Buttons */}
      <ShowcaseDemoSection
        title="With First/Last Buttons"
        description="Optional buttons to jump to the first or last page"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">With first/last buttons enabled</Text>
              <Pagination
                page={8}
                totalPages={25}
                onPageChange={staticPageChange}
                showFirstLast={true}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">At first page (first button disabled)</Text>
              <Pagination
                page={1}
                totalPages={25}
                onPageChange={staticPageChange}
                showFirstLast={true}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">At last page (last button disabled)</Text>
              <Pagination
                page={25}
                totalPages={25}
                onPageChange={staticPageChange}
                showFirstLast={true}
              />
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Items Info */}
      <ShowcaseDemoSection
        title="With Items Info"
        description="Display 'Showing X-Y of Z items' for context"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">Policy list pagination</Text>
              <Pagination
                page={3}
                totalPages={12}
                totalItems={287}
                pageSize={25}
                onPageChange={staticPageChange}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Claims list with larger page size</Text>
              <Pagination
                page={1}
                totalPages={5}
                totalItems={234}
                pageSize={50}
                onPageChange={staticPageChange}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Customer search results</Text>
              <Pagination
                page={8}
                totalPages={42}
                totalItems={1047}
                pageSize={25}
                onPageChange={staticPageChange}
                showFirstLast={true}
              />
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Page Size Selector */}
      <ShowcaseDemoSection
        title="With Page Size Selector"
        description="Allow users to change the number of items per page"
       
      >
          <VStack gap={4}>
            <Pagination
              page={controlledPage}
              totalPages={Math.ceil(487 / pageSize)}
              totalItems={487}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={(page) => setControlledPage(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setControlledPage(1); // Reset to first page when changing page size
              }}
              showFirstLast={true}
            />
            <HStack gap={4}>
              <Text size="sm" color="secondary">
                Page {controlledPage} of {Math.ceil(487 / pageSize)}
              </Text>
              <Text size="sm" color="secondary">
                ({pageSize} items per page)
              </Text>
            </HStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Controlled Pagination */}
      <ShowcaseDemoSection
        title="Controlled Pagination"
        description="Pagination state managed by parent component"
       
      >
          <VStack gap={4}>
            <Card className="p-4">
              <VStack gap={3}>
                <Text weight="medium">Insurance Policies</Text>
                <Text size="sm" color="secondary">
                  Viewing policies {(currentPage - 1) * 25 + 1} - {Math.min(currentPage * 25, 487)} of 487 total
                </Text>
                <div className="h-32 bg-[#F5F4F2] rounded flex items-center justify-center">
                  <Text color="secondary">Policy list content for page {currentPage}</Text>
                </div>
              </VStack>
            </Card>
            <Pagination
              page={currentPage}
              totalPages={20}
              totalItems={487}
              pageSize={25}
              onPageChange={setCurrentPage}
              showFirstLast={true}
            />
          </VStack>
      </ShowcaseDemoSection>

      {/* Sibling Count */}
      <ShowcaseDemoSection
        title="Sibling Count Variations"
        description="Control how many page numbers appear around the current page"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">siblingCount=1 (default) - shows 1 page on each side</Text>
              <Pagination
                page={10}
                totalPages={20}
                onPageChange={staticPageChange}
                siblingCount={1}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">siblingCount=2 - shows 2 pages on each side</Text>
              <Pagination
                page={10}
                totalPages={20}
                onPageChange={staticPageChange}
                siblingCount={2}
              />
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">siblingCount=0 - minimal page numbers</Text>
              <Pagination
                page={10}
                totalPages={20}
                onPageChange={staticPageChange}
                siblingCount={0}
              />
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* Insurance Domain Examples */}
      <ShowcaseDemoSection
        title="Insurance Domain Examples"
        description="Real-world pagination scenarios in insurance applications"
       
      >
          <VStack gap={6}>
            <Card className="p-4">
              <VStack gap={3}>
                <Text weight="medium">Claims Processing Queue</Text>
                <div className="h-20 bg-[#F5F4F2] rounded flex items-center justify-center">
                  <Text size="sm" color="secondary">156 pending claims to review</Text>
                </div>
                <Pagination
                  page={1}
                  totalPages={7}
                  totalItems={156}
                  pageSize={25}
                  pageSizeOptions={[10, 25, 50]}
                  onPageChange={staticPageChange}
                  onPageSizeChange={staticPageSizeChange}
                />
              </VStack>
            </Card>

            <Card className="p-4">
              <VStack gap={3}>
                <Text weight="medium">Policy Search Results</Text>
                <div className="h-20 bg-[#F5F4F2] rounded flex items-center justify-center">
                  <Text size="sm" color="secondary">Search: "auto insurance california"</Text>
                </div>
                <Pagination
                  page={3}
                  totalPages={89}
                  totalItems={2214}
                  pageSize={25}
                  onPageChange={staticPageChange}
                  showFirstLast={true}
                />
              </VStack>
            </Card>

            <Card className="p-4">
              <VStack gap={3}>
                <Text weight="medium">Customer Transaction History</Text>
                <div className="h-20 bg-[#F5F4F2] rounded flex items-center justify-center">
                  <Text size="sm" color="secondary">Premium payments for POL-2024-001842</Text>
                </div>
                <Pagination
                  page={1}
                  totalPages={3}
                  totalItems={28}
                  pageSize={10}
                  onPageChange={staticPageChange}
                />
              </VStack>
            </Card>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const PaginationShowcase: ShowcaseDefinition = {
  id: 'pagination',
  name: 'Pagination',
  category: 'navigation',
  description: 'Page navigation for data lists and tables',
  propsDefinition: [
    {
      name: 'showFirstLast',
      type: 'boolean',
      defaultValue: false,
      description: 'Show buttons to jump to first/last page',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'nav element with role="navigation"',
      'aria-label="Pagination" for navigation context',
      'aria-current="page" on current page button',
      'aria-label on navigation buttons (First, Previous, Next, Last)',
    ],
    keyboardNav: [
      'Tab: Navigate between pagination controls',
      'Enter/Space: Activate page number or navigation button',
      'Page buttons are disabled at boundaries (prev on first, next on last)',
    ],
    notes: [
      'Current page button is disabled to prevent redundant clicks',
      'Ellipsis elements are aria-hidden',
      'Items count provides context for screen readers',
      'Page size selector has aria-label for accessibility',
    ],
  },
  component: PaginationShowcaseComponent,
};
